import { Environments, getEnvironment } from '../utils/environment';

import { RegisteredApplications } from '../types';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useIsCypress } from '../utils/isCypress';
import { useEffect } from 'react';
const ALLOW_LOCALHOST = localStorage.getItem('DD_ALLOW_LOCALHOST') === 'true';

type RumCredentials = { applicationId: string; clientToken: string };

const RUM_CREDENTIALS: Partial<Record<RegisteredApplications, RumCredentials>> =
	{
		[RegisteredApplications.APP_ONU_MAIN]: {
			applicationId: '85995cb9-a3e2-4d11-b401-16f3c153d577',
			clientToken: 'pubda0196aee136087bc1c69fc78d0f14c9',
		},
		[RegisteredApplications.APP_AM_MAIN]: {
			applicationId: '787d9dce-7a9f-41ce-b119-6675c1234b41',
			clientToken: 'pub2add4db26d2d7452b538bafec77fc5fd',
		},
	};

export function useInitDataDog(
	registeredApplication: RegisteredApplications | string,
) {
	const isCypress = useIsCypress();
	const isLocalHost = getEnvironment() === Environments.LOCAL;
	const isThisHostAllowed = ALLOW_LOCALHOST || !isLocalHost;
	const { applicationId, clientToken } =
		RUM_CREDENTIALS[registeredApplication] ?? {};

	useEffect(() => {
		try {
			if (!isCypress && isThisHostAllowed) {
				if (!applicationId || !clientToken) {
					console.warn(
						'[Datadog] Skipping initialization due to missing Application ID or Client Token.',
					);
					return;
				}

				datadogLogs.init({
					clientToken: 'pub7d6a245f499f54cf462364ef11e9693b', // Universal token
					site: 'datadoghq.com',
					forwardErrorsToLogs: true,
					sampleRate: 100,
					service: registeredApplication,
					env: getEnvironment(),
					silentMultipleInit: false,
					trackInteractions: true,
					useCrossSiteSessionCookie: true,
				});

				datadogRum.init({
					applicationId,
					clientToken,
					site: 'datadoghq.com',
					service: registeredApplication,
					env: getEnvironment(),
					// Specify a version number to identify the deployed version of your application in Datadog
					// version: '1.0.0',
					sampleRate: 100,
					trackInteractions: true,
					silentMultipleInit: true,
					// Add additional tracing origins. Each service needs allow-listed x-dd headers.
					// Check in saas-idp-router repo for examples.
					allowedTracingOrigins: [
						/https:\/\/saas-idp-router.info360.*.com/,
					],
				});
			}
		} catch (e) {
			console.error('DataDog Init Error', e);
		}
	}, [isCypress, isThisHostAllowed, applicationId, clientToken]);
}

export const useDatadogLogs = () => {
	return datadogLogs;
};
